import React, { useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { Button } from "@/shared/ui";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface CheckoutFormProps {
  clientSecret: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  clientSecret,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Step 1: Submit elements to validate form data
      // This must be called before confirmPayment()
      const { error: submitError } = await elements.submit();

      if (submitError) {
        onError(submitError.message || "Form validation failed");
        setProcessing(false);
        return;
      }

      // Step 2: Confirm payment after successful validation
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/dashboard?payment=success`,
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Payment failed");
        setProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        toast.success(t("payments.checkout.paymentProcessed"));
        onSuccess();
      } else {
        // Payment might require additional action or is still processing
        setProcessing(false);
      }
    } catch (err: any) {
      onError(err.message || "An error occurred");
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white border border-tiber-200 rounded-lg p-4">
        <PaymentElement
          options={{
            layout: "tabs",
          }}
        />
      </div>

      <Button
        type="submit"
        variant="primary"
        disabled={!stripe || processing}
        isLoading={processing}
        className="w-full"
        size="lg"
        leftIcon={<CreditCard className="w-5 h-5" />}
      >
        {processing
          ? t("payments.checkout.processing")
          : t("payments.checkout.confirmPayment")}
      </Button>
    </form>
  );
};
